/**
 * Localisation FR/EN des OPTIONS de listes fermées des personae.
 *
 * Ces champs (CSP, critères/phase d'achat, relation produit, raison de contact,
 * style d'expression, attente principale, fonction) sont stockés EN FRANÇAIS
 * (la valeur sélectionnée = le libellé FR de la liste, cf. PersonaUtils.tsx).
 * En mode EN ils s'affichaient donc en FR. Ce helper fournit la traduction.
 *
 * Principe (règle Valérie 18/06) : tout libellé affiché doit exister en FR ET EN.
 * Pour une valeur hors liste (saisie custom par un user), on renvoie la valeur
 * telle quelle (fallback) — on ne traduit que les options connues.
 *
 * Pendant qu'on est en FR, la valeur stockée EST déjà le libellé FR → retour direct.
 */

// Paires FR → EN. Les libellés FR sont uniques toutes listes confondues → un seul map suffit.
const PERSONA_OPTION_FR_TO_EN: Record<string, string> = {
  // CSP (B2C)
  "Agriculteur exploitant": "Farmer",
  "Artisan": "Craftsman",
  "Commerçant": "Shopkeeper",
  "Chef d'entreprise": "Business owner",
  "Cadre et profession intellectuelle supérieure": "Executive / senior professional",
  "Profession intermédiaire": "Intermediate profession",
  "Employé": "Employee",
  "Ouvrier": "Manual worker",
  "Retraité": "Retired",
  "Étudiant": "Student",
  // Critères d'achat (B2C)
  "Prix": "Price",
  "Qualité": "Quality",
  "Marque": "Brand",
  "Avis": "Reviews",
  "Praticité": "Convenience",
  // Phase d'achat (B2C)
  "Réflexion": "Consideration",
  "Recherche d'infos": "Information gathering",
  "Décision": "Decision",
  "Déjà client": "Existing customer",
  // Relation au produit (B2C)
  "Problème ou besoin principal à résoudre": "Main problem or need to solve",
  "Frustrations/points de douleur actuels": "Current frustrations / pain points",
  "Motivation d'achat (gain de temps, plaisir, statut, économie)": "Purchase motivation (time savings, pleasure, status, savings)",
  "Freins potentiels (prix, confiance, complexité)": "Potential barriers (price, trust, complexity)",
  // Raison de contact (Customer Care)
  "Panne": "Breakdown",
  "Dysfonctionnement": "Malfunction",
  "Incompréhension produit/service": "Product/service misunderstanding",
  "Mécontentement livraison": "Delivery dissatisfaction",
  "Demande de remboursement": "Refund request",
  // Style d'expression (Customer Care)
  "Laconique": "Terse",
  "Bavard": "Talkative",
  "Précis et technique": "Precise and technical",
  "Confus": "Confused",
  "Emotionnel": "Emotional",
  // Attente principale (Customer Care)
  "Résolution rapide": "Quick resolution",
  "Explication": "Explanation",
  "Compensation": "Compensation",
  "Réassurance": "Reassurance",
  // Fonction (B2B) — liste fermée ; valeurs custom renvoyées telles quelles
  "Directeur Général / CEO": "CEO / General Manager",
  "Directeur Commercial": "Sales Director",
  "Directeur Marketing": "Marketing Director",
  "Directeur Financier / CFO": "CFO / Finance Director",
  "Directeur des Achats": "Purchasing Director",
  "Directeur Technique / CTO": "CTO / Technical Director",
  "Directeur des Ressources Humaines": "HR Director",
  "Directeur des Opérations / COO": "COO / Operations Director",
  "Responsable IT / DSI": "IT Manager / CIO",
  "Chef de projet": "Project Manager",
  "Responsable Qualité": "Quality Manager",
  "Responsable Logistique": "Logistics Manager",
};

/**
 * Traduit une option de persona en liste fermée selon la langue.
 * @param value valeur stockée (libellé FR) — peut être null/undefined ou custom
 * @param lang  "fr" | "en" (ou i18n.language)
 */
export function localizePersonaOption(value: string | null | undefined, lang: string): string | undefined {
  if (!value) return value ?? undefined;
  if (!lang || !lang.toLowerCase().startsWith("en")) return value; // FR : valeur déjà en FR
  return PERSONA_OPTION_FR_TO_EN[value.trim()] ?? value; // EN : trad si connue, sinon valeur custom
}
